import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  CloudUpload as UploadIcon,
  Public as PublicIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { PageHeader, LoadingSpinner, WedmsStatusChip } from '../../components/common';
import { useTemplateViewModel } from '../../viewmodels';
import { Template } from '../../models';

const TemplateListPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    templates,
    isLoading,
    uploadTemplate,
    isUploading,
    deleteTemplate,
    isDeleting,
    refetch,
  } = useTemplateViewModel();

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // 처리 중인 템플릿이 있으면 주기적으로 목록 새로 고침
  useEffect(() => {
    const hasProcessingTemplates = templates.some(
      (t) => t.wedms_status === 'processing' || t.wedms_status === 'pending'
    );

    if (hasProcessingTemplates) {
      // 3초마다 새로 고침
      if (!pollingIntervalRef.current) {
        pollingIntervalRef.current = setInterval(async () => {
          try {
            await refetch();
          } catch (error: unknown) {
            // 401 에러 시 폴링 중단 (인증 만료)
            if (error && typeof error === 'object' && 'response' in error) {
              const axiosError = error as { response?: { status?: number } };
              if (axiosError.response?.status === 401) {
                console.warn('Authentication expired, stopping template polling');
                if (pollingIntervalRef.current) {
                  clearInterval(pollingIntervalRef.current);
                  pollingIntervalRef.current = null;
                }
              }
            }
          }
        }, 3000);
      }
    } else {
      // 처리 중인 템플릿이 없으면 폴링 중지
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [templates, refetch]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [uploadForm, setUploadForm] = useState({
    name: '',
    description: '',
    previewFile: null as File | null,     // 배경용 HWPX (PDF 미리보기용)
    generatorFile: null as File | null,   // 생성용 HWPX (<<__필드명__>> 포함)
  });
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleUpload = async () => {
    if (!uploadForm.name) {
      setUploadError('템플릿명을 입력하세요.');
      return;
    }
    if (!uploadForm.previewFile && !uploadForm.generatorFile) {
      setUploadError('배경용 HWPX 또는 생성용 HWPX 중 하나 이상을 선택하세요.');
      return;
    }

    try {
      await uploadTemplate({
        name: uploadForm.name,
        description: uploadForm.description,
        previewFile: uploadForm.previewFile || undefined,
        generatorFile: uploadForm.generatorFile || undefined,
        fieldMapping: { fields: [] },
      });
      setUploadDialogOpen(false);
      setUploadForm({ name: '', description: '', previewFile: null, generatorFile: null });
      setUploadError(null);
    } catch (error) {
      setUploadError('템플릿 업로드에 실패했습니다.');
    }
  };

  const handleDelete = async () => {
    if (!selectedTemplate) return;
    try {
      deleteTemplate(selectedTemplate.id);
      setDeleteDialogOpen(false);
      setSelectedTemplate(null);
    } catch (error) {
      console.error('Failed to delete template:', error);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Box>
      <PageHeader
        title="템플릿 관리"
        breadcrumbs={[{ label: '템플릿' }]}
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setUploadDialogOpen(true)}
          >
            템플릿 업로드
          </Button>
        }
      />

      <Card>
        <CardContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            HWPX 형식의 문서 템플릿을 업로드하세요. 템플릿 내 {'<<__필드명__>>'} 형식의 텍스트가 자동으로 치환됩니다.
          </Alert>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>템플릿명</TableCell>
                  <TableCell>설명</TableCell>
                  <TableCell>배경용 HWPX</TableCell>
                  <TableCell>생성용 HWPX</TableCell>
                  <TableCell>WeDMS</TableCell>
                  <TableCell>상태</TableCell>
                  <TableCell>공유</TableCell>
                  <TableCell>등록일</TableCell>
                  <TableCell align="right">작업</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography color="text.secondary" sx={{ py: 4 }}>
                        등록된 템플릿이 없습니다.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  templates.map((template) => (
                    <TableRow key={template.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {template.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                          {template.description || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {template.has_preview ? (
                          <Box>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
                              {template.preview_filename}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatFileSize(template.preview_file_size || 0)}
                            </Typography>
                          </Box>
                        ) : template.has_hwpx ? (
                          <Box>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
                              {template.filename}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatFileSize(template.file_size || 0)} (레거시)
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {template.has_generator ? (
                          <Box>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
                              {template.generator_filename}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatFileSize(template.generator_file_size || 0)}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <WedmsStatusChip
                          status={template.wedms_status}
                          docId={template.wedms_doc_id}
                          error={template.wedms_error}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={template.is_active ? '활성' : '비활성'}
                          size="small"
                          color={template.is_active ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={template.is_shared ? '모든 사용자가 사용 가능' : '본인만 사용 가능'}>
                          <Chip
                            icon={template.is_shared ? <PublicIcon /> : <LockIcon />}
                            label={template.is_shared ? '공유' : '비공개'}
                            size="small"
                            color={template.is_shared ? 'info' : 'default'}
                            variant="outlined"
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {new Date(template.created_at).toLocaleDateString('ko-KR')}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/templates/${template.id}`)}
                          title="상세보기"
                        >
                          <ViewIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/templates/${template.id}/edit`)}
                          title="편집"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedTemplate(template);
                            setDeleteDialogOpen(true);
                          }}
                          title="삭제"
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* 업로드 다이얼로그 */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>템플릿 업로드</DialogTitle>
        <DialogContent>
          {uploadError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {uploadError}
            </Alert>
          )}
          <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
            <Typography variant="body2" gutterBottom>
              <strong>배경용 HWPX:</strong> PDF 미리보기 배경으로 사용됩니다 (WeDMS에서 PDF로 변환)
            </Typography>
            <Typography variant="body2">
              <strong>생성용 HWPX:</strong> {'<<__필드명__>>'} 플레이스홀더를 포함하여 문서 생성에 사용됩니다
            </Typography>
          </Alert>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="템플릿명"
              value={uploadForm.name}
              onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="설명"
              value={uploadForm.description}
              onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
              multiline
              rows={2}
              fullWidth
            />
            {/* 배경용 HWPX */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                배경용 HWPX (PDF 미리보기용)
              </Typography>
              <input
                type="file"
                accept=".hwpx,.hwp"
                onChange={(e) => setUploadForm({ ...uploadForm, previewFile: e.target.files?.[0] || null })}
                style={{ display: 'none' }}
                id="preview-file-upload"
              />
              <label htmlFor="preview-file-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadIcon />}
                  fullWidth
                  color={uploadForm.previewFile ? 'success' : 'primary'}
                >
                  {uploadForm.previewFile ? uploadForm.previewFile.name : '배경용 HWPX 선택'}
                </Button>
              </label>
            </Box>
            {/* 생성용 HWPX */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                생성용 HWPX ({'<<__필드명__>>'} 포함)
              </Typography>
              <input
                type="file"
                accept=".hwpx,.hwp"
                onChange={(e) => setUploadForm({ ...uploadForm, generatorFile: e.target.files?.[0] || null })}
                style={{ display: 'none' }}
                id="generator-file-upload"
              />
              <label htmlFor="generator-file-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadIcon />}
                  fullWidth
                  color={uploadForm.generatorFile ? 'success' : 'primary'}
                >
                  {uploadForm.generatorFile ? uploadForm.generatorFile.name : '생성용 HWPX 선택'}
                </Button>
              </label>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>취소</Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={isUploading || (!uploadForm.previewFile && !uploadForm.generatorFile) || !uploadForm.name}
          >
            {isUploading ? '업로드 중...' : '업로드'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 삭제 확인 다이얼로그 */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>템플릿 삭제</DialogTitle>
        <DialogContent>
          <Typography>
            '{selectedTemplate?.name}' 템플릿을 삭제하시겠습니까?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            이 작업은 되돌릴 수 없습니다.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>취소</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? '삭제 중...' : '삭제'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplateListPage;
